/**
 * Shared membership of the server's /map personnel room (P0-13 purpose rooms).
 *
 * socket.io-client returns ONE cached Socket per namespace and origin, so the
 * app-shell SOS alarm (useSosAlarm) and the dashboards' usePersonnelSocket talk
 * through the same server connection and the same room membership. A plain
 * `leave:personnel` from one of them would take the other out of the room too:
 * leaving the Command dashboard silently downgraded the console's SOS alarm to
 * the presence-only payload (no location, no message) until the next reconnect
 * (wave-10 residual 106).
 *
 * Each user of the room CLAIMS it; the room is joined on every claim (the
 * server checks personnel_tracking:view each time, and a join is idempotent)
 * and `leave:personnel` is sent only when the LAST claim on that socket is
 * released. Claims are per Socket object, so two distinct connections never
 * affect each other. A claim is taken while connected and released on
 * disconnect (the server forgets rooms with the connection), so callers claim
 * again after a reconnect.
 */

export interface PersonnelRoomSocket {
  emit: (event: string, ...args: unknown[]) => unknown;
  connected?: boolean;
}

const claims = new WeakMap<object, number>();

/** Join the personnel room for this caller. Returns the release (idempotent). */
export function claimPersonnelRoom(socket: PersonnelRoomSocket): () => void {
  claims.set(socket, (claims.get(socket) ?? 0) + 1);
  socket.emit('join:personnel');
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const left = (claims.get(socket) ?? 1) - 1;
    if (left > 0) {
      claims.set(socket, left);
      return;
    }
    claims.delete(socket);
    if (socket.connected) socket.emit('leave:personnel');
  };
}

/** How many callers hold the room on this socket (tests and diagnostics). */
export function personnelRoomClaimCount(socket: PersonnelRoomSocket): number {
  return claims.get(socket) ?? 0;
}
